export const heroData = {
  name: "ADARSH RAJ",
  role: "Applied AI & Backend Engineer",
  typingText: "< APPLIED AI & BACKEND ENGINEER />",
  description:
    "I design and ship production AI systems, developer-grade backend services, and enterprise data platforms that turn complex governance workflows into reliable product experiences.",
  company: {
    name: "GSK",
    url: "https://www.gsk.com/",
  },
  avatar: {
    src: "/avatar.png",
    alt: "Adarsh Raj",
  },
  expertisePills: [
    "Enterprise AI",
    "LLM Platforms",
    "RAG Systems",
    "FastAPI",
    "Databricks",
    "Azure AI",
    "Backend Architecture",
  ],
  expertise: [
    "Enterprise AI",
    "LLM Platforms",
    "RAG Systems",
    "FastAPI",
    "Databricks",
    "Azure AI",
    "Backend Architecture",
  ],
  metrics: [
    {
      value: "76K+",
      label: "Enterprise Assets Indexed",
    },
    {
      value: "99.6%",
      label: "Faster Governance Sync",
    },
    {
      value: "87.5%",
      label: "Metadata Retrieval Gain",
    },
    {
      value: "20+",
      label: "Production Releases",
    },
  ],
  social: {
    email: "mailto:adarsh.Raj.2004@outlook.com",
    linkedin: "https://linkedin.com/in/adarsh-raj04",
    github: "https://github.com/adarsh-raj04",
    portfolio: "https://adarshraj.in",
  },
  ctas: [
    {
      label: "Explore Projects",
      href: "#enterprise-projects",
      variant: "primary",
    },
    {
      label: "Download Resume",
      href: "/Adarsh_Raj_Resume.pdf",
      variant: "secondary",
      download: true,
    },
  ],
};

export const aboutData = {
  title: "ABOUT",
  subtitle: "Engineering depth with product ownership",
  intro:
    "I operate at the intersection of AI product engineering, backend systems, and enterprise platform reliability.",
  sections: [
    {
      heading: "Who I Am",
      color: "text-cyan-400",
      content:
        "Applied AI and backend engineer building robust data and intelligence platforms for high-scale enterprise use cases.",
    },
    {
      heading: "What I Build",
      color: "text-purple-400",
      content:
        "Semantic search, LLM-assisted workflows, metadata intelligence APIs, and cloud-native pipelines that teams can trust in production.",
    },
    {
      heading: "Beyond Work",
      color: "text-pink-400",
      content:
        "I build personal AI products, study distributed systems, and experiment with practical ML tooling that improves engineering velocity.",
    },
  ],
  expertise: [
    "AI Platform Engineering",
    "FastAPI Service Design",
    "Search and Retrieval",
    "Data Governance Automation",
    "Cloud Data Orchestration",
    "CI/CD and Reliability",
  ],
  stats: [
    {
      value: 76,
      display: "76K+",
      label: "ASSETS GOVERNED",
      color: "text-cyan-400",
    },
    {
      value: 200,
      display: "200+",
      label: "HOURS SAVED / YEAR",
      color: "text-purple-400",
    },
    {
      value: 99.6,
      display: "99.6%",
      label: "FASTER GOVERNANCE SYNC",
      color: "text-pink-400",
    },
    {
      value: 87.5,
      display: "87.5%",
      label: "RETRIEVAL IMPROVEMENT",
      color: "text-blue-400",
    },
  ],
  bento: {
    whoIAm: {
      title: "Who I Am",
      description:
        "Applied AI and backend engineer building robust data and intelligence platforms for high-scale enterprise use cases.",
    },
    whatIBuild: {
      title: "What I Build",
      description:
        "Semantic search, LLM-assisted workflows, metadata intelligence APIs, and cloud-native pipelines that teams can trust in production.",
    },
    beyondWork: {
      title: "Beyond Work",
      description:
        "I build personal AI products, study distributed systems, and experiment with practical ML tooling that improves engineering velocity.",
    },
    coreExpertise: {
      title: "Core Expertise",
      items: [
        "AI Platform Engineering",
        "FastAPI Service Design",
        "Search and Retrieval",
        "Data Governance Automation",
        "Cloud Data Orchestration",
        "CI/CD and Reliability",
      ],
    },
    impactMetrics: {
      title: "Impact Metrics",
      items: [
        {
          value: "76K+",
          label: "Assets Governed",
        },
        {
          value: "200+",
          label: "Hours Saved / Year",
        },
        {
          value: "30m -> 7s",
          label: "Sync Time Reduction",
        },
        {
          value: "8h -> 1h",
          label: "Metadata Query Window",
        },
      ],
    },
  },
};

export const experienceData = {
  title: "PROFESSIONAL EXPERIENCE",
  intro:
    "From intern to full-time engineer, I have led AI and backend initiatives that modernized enterprise data governance at scale.",
  roles: [
    {
      company: "GSK (GlaxoSmithKline)",
      role: "Data and AI Engineer",
      duration: "January 2024 - Present",
      location: "Bengaluru, Karnataka, India",
      summary:
        "Owning AI platform capabilities, metadata services, and cloud data products powering governance and discovery across the enterprise.",
      enterprisePlatforms: [
        "Rover Metadata Platform",
        "Augmented Data Quality",
        "Business Glossary Automation",
      ],
      coreEngineering: [
        "Designed semantic search and conversational AI on OpenAI embeddings and Azure AI Search.",
        "Built production FastAPI microservices for metadata retrieval, lineage, and enterprise search.",
        "Optimized Spark ingestion pipelines on Databricks for scale and faster runtime.",
        "Engineered ServiceNow-Collibra and SailPoint-Rover integrations.",
      ],
      businessImpact: [
        "Eliminated 90% manual governance effort with end-to-end workflow automation.",
        "Reduced data sync time by 99.6% from 30 min to 7 sec.",
        "Improved metadata retrieval by 87.5% from 8 hours to 1 hour.",
        "Enabled transparent quality reporting through automated DQ metric registration.",
      ],
      technologyStack: [
        "Python",
        "FastAPI",
        "OpenAI",
        "Azure AI Search",
        "Databricks",
        "Apache Spark",
        "Azure Functions",
        "Azure Data Factory",
        "Azure SQL",
        "Collibra",
        "ServiceNow",
        "SailPoint",
        "GitHub Actions",
        "Azure DevOps",
      ],
    },
  ],
};

export const enterpriseProjects = {
  title: "ENTERPRISE PROJECTS",
  subtitle: "Production systems built for business-critical workflows",
  projects: [
    {
      title: "Rover",
      problem:
        "Enterprise metadata discovery was fragmented across tools, making governance and search slow and inconsistent.",
      solution:
        "Built semantic search and conversational access for metadata with retrieval services and governance-aware indexing.",
      architecture:
        "OpenAI embeddings + Azure AI Search + FastAPI service layer + Databricks ingestion pipelines + Collibra integration.",
      techStack: [
        "Python",
        "FastAPI",
        "OpenAI",
        "Azure AI Search",
        "Databricks",
        "Apache Spark",
        "Collibra",
      ],
      impact: [
        "Improved discoverability across 76K+ enterprise assets.",
        "Enabled conversational metadata workflows for cross-functional users.",
      ],
      github: "",
      liveDemo: "",
      screenshotPlaceholder: "Rover product screenshot",
    },
    {
      title: "Augmented DQ",
      problem:
        "Data quality outputs were disconnected from governance tooling, reducing visibility and trust.",
      solution:
        "Integrated DQ outputs into Collibra with automated registration and governance reporting flows.",
      architecture:
        "Data quality producers + integration services + Collibra APIs + scheduled cloud orchestration.",
      techStack: [
        "Python",
        "Azure Functions",
        "Collibra",
        "Azure Data Factory",
        "Azure SQL",
      ],
      impact: [
        "Created transparent quality visibility for governed metadata assets.",
        "Reduced manual quality reporting and compliance effort.",
      ],
      github: "",
      liveDemo: "",
      screenshotPlaceholder: "Augmented DQ dashboard screenshot",
    },
    {
      title: "Business Glossary",
      problem:
        "Glossary creation and enrichment were manual, slow, and inconsistent across teams.",
      solution:
        "Built AI-assisted workflows to generate and enrich glossary artifacts with metadata context.",
      architecture:
        "LLM-assisted generation + metadata retrieval APIs + approval workflow integration.",
      techStack: ["Python", "FastAPI", "OpenAI", "Collibra", "Azure"],
      impact: [
        "Accelerated glossary creation lifecycle and reduced manual dependency.",
        "Increased consistency in enterprise business definitions.",
      ],
      github: "",
      liveDemo: "",
      screenshotPlaceholder: "Business Glossary workflow screenshot",
    },
  ],
};

export const personalProjects = {
  title: "PERSONAL PROJECTS",
  subtitle: "Independent builds focused on practical AI products",
  projects: [
    {
      title: "Mnemo",
      problem:
        "Knowledge workers struggle to retain and retrieve context across fragmented notes and resources.",
      solution:
        "Designed a memory-oriented AI workspace with semantic recall and contextual assistant workflows.",
      architecture:
        "React frontend + FastAPI backend + vector retrieval + model orchestration + cloud deployment.",
      techStack: ["React", "FastAPI", "Python", "OpenAI", "Vector Database"],
      impact: [
        "Improved information recall and contextual retrieval for heavy research workflows.",
      ],
      github: "https://github.com/adarsh-raj04",
      liveDemo: "",
      screenshotPlaceholder: "Mnemo product screenshot",
    },
    {
      title: "MedResearch",
      problem:
        "Medical research synthesis requires navigating dense documents with high cognitive overhead.",
      solution:
        "Built an AI-assisted exploration interface for summarization, retrieval, and structured insight extraction.",
      architecture:
        "Document ingestion + semantic indexing + prompt workflows + interactive result surfaces.",
      techStack: ["Python", "React", "LLMs", "Search Index", "NLP"],
      impact: [
        "Reduced time-to-insight for exploratory research and technical review tasks.",
      ],
      github: "https://github.com/adarsh-raj04",
      liveDemo: "",
      screenshotPlaceholder: "MedResearch interface screenshot",
    },
  ],
};

export const skillsData = {
  title: "TECHNICAL SKILLS",
  subtitle: "Capabilities grouped by engineering discipline",
  groups: [
    {
      category: "Programming",
      items: ["Python", "JavaScript", "TypeScript", "SQL"],
    },
    {
      category: "Backend",
      items: ["FastAPI", "REST API Design", "Microservices", "GraphQL"],
    },
    {
      category: "AI",
      items: [
        "LLMs",
        "RAG",
        "Embeddings",
        "Prompt Engineering",
        "Semantic Search",
      ],
    },
    {
      category: "Data Engineering",
      items: ["Databricks", "Apache Spark", "ETL Pipelines", "Data Modeling"],
    },
    {
      category: "Cloud",
      items: [
        "Azure Functions",
        "Azure Data Factory",
        "Azure AI Search",
        "Azure SQL",
      ],
    },
    {
      category: "Databases",
      items: ["PostgreSQL", "MySQL", "Azure SQL", "Vector Stores"],
    },
    {
      category: "DevOps",
      items: [
        "GitHub Actions",
        "Azure DevOps",
        "CI/CD",
        "Docker",
        "Monitoring",
      ],
    },
  ],
};

export const awardsData = {
  title: "AWARDS",
  subtitle: "Selected recognition and performance highlights",
  items: [
    {
      title: "Enterprise Impact Recognition",
      issuer: "GSK",
      year: "2025",
      description:
        "Recognized for high-impact automation and measurable governance acceleration across metadata programs.",
    },
    {
      title: "Engineering Excellence Spotlight",
      issuer: "GSK",
      year: "2024",
      description:
        "Acknowledged for shipping reliable AI services and leading cross-platform technical integration efforts.",
    },
  ],
};

export const educationData = {
  title: "EDUCATION",
  subtitle: "Academic foundation for applied engineering",
  degree: "Bachelor of Technology in Computer Science",
  institution: "M S Ramaiah University of Applied Sciences",
  duration: "2020 - 2024",
  location: "Bengaluru, India",
  summary:
    "Focused on software engineering, data systems, and applied machine learning with strong project-first execution.",
  highlights: [
    "Built production-oriented academic and independent engineering projects.",
    "Applied data structures, distributed systems, and database concepts in practical products.",
  ],
};

export const certificationsData = {
  title: "CERTIFICATIONS",
  subtitle: "Validated skills across AI, cloud, and engineering",
  maxVisible: 6,
  items: [
    {
      name: "Microsoft Certified: Azure AI Engineer Associate",
      issuer: "Microsoft",
      year: "2025",
      credentialUrl: "",
    },
    {
      name: "Databricks Fundamentals",
      issuer: "Databricks",
      year: "2024",
      credentialUrl: "",
    },
    {
      name: "Azure Data Fundamentals",
      issuer: "Microsoft",
      year: "2024",
      credentialUrl: "",
    },
    {
      name: "Google Data Analytics",
      issuer: "Google",
      year: "2023",
      credentialUrl: "",
    },
    {
      name: "Python for Everybody",
      issuer: "University of Michigan",
      year: "2023",
      credentialUrl: "",
    },
    {
      name: "DevOps Essentials",
      issuer: "IBM",
      year: "2023",
      credentialUrl: "",
    },
  ],
};

export const contactData = {
  title: "CONTACT",
  subtitle: "Let us build something high-leverage",
  description:
    "I am open to high-impact engineering roles, AI platform collaborations, and product-focused backend opportunities.",
  channels: [
    {
      label: "Email",
      value: "adarsh.Raj.2004@outlook.com",
      href: "mailto:adarsh.Raj.2004@outlook.com",
    },
    {
      label: "LinkedIn",
      value: "linkedin.com/in/adarsh-raj04",
      href: "https://linkedin.com/in/adarsh-raj04",
    },
    {
      label: "GitHub",
      value: "github.com/adarsh-raj04",
      href: "https://github.com/adarsh-raj04",
    },
    {
      label: "Portfolio",
      value: "adarshraj.in",
      href: "https://adarshraj.in",
    },
    {
      label: "Resume",
      value: "Download PDF",
      href: "/Adarsh_Raj_Resume.pdf",
    },
  ],
  formspreeEndpoint: "https://formspree.io/f/xwpkynkj",
};
